import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EVENT_TYPES, EVENT_TYPE_META } from '@/constants/calendarMeta';

const blank = { title: '', date: '', end_date: '', type: 'meeting', start_time: '', notes: '', scope: 'team' };

export const EventDialog = ({ open, onOpenChange, onSave, event, defaultDate, canManage }) => {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(event
      ? { ...blank, ...event, scope: event.scope || 'team' }
      : { ...blank, date: defaultDate || '', scope: canManage ? 'team' : 'personal' });
  }, [open, event, defaultDate, canManage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(form, event);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>{event ? 'Edit Event' : 'New Event'}</DialogTitle>
          <DialogDescription>Shared schedule for the whole team.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[hsl(var(--text-muted))]">Title *</Label>
            <Input value={form.title} maxLength={180} onChange={(e) => set('title', e.target.value)}
              placeholder="Weekly sync, Fira Game briefing…" className="mt-1.5" data-testid="event-title-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))]">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)}
                className="mt-1.5" data-testid="event-date-input" />
            </div>
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))]">End date</Label>
              <Input type="date" value={form.end_date || ''} onChange={(e) => set('end_date', e.target.value)}
                className="mt-1.5" data-testid="event-end-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))]">Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className="mt-1.5" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`event-type-${t}`}>{EVENT_TYPE_META[t].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))]">Time</Label>
              <Input type="time" value={form.start_time || ''} onChange={(e) => set('start_time', e.target.value)}
                className="mt-1.5" data-testid="event-time-input" />
            </div>
          </div>

          {canManage && (
            <div>
              <Label className="text-xs text-[hsl(var(--text-muted))]">Visibility</Label>
              <Select value={form.scope} onValueChange={(v) => set('scope', v)}>
                <SelectTrigger className="mt-1.5" data-testid="event-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="team" data-testid="event-scope-team">Whole team</SelectItem>
                  <SelectItem value="personal" data-testid="event-scope-personal">Only me</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-[hsl(var(--text-muted))]">Notes</Label>
            <Textarea value={form.notes || ''} rows={3} onChange={(e) => set('notes', e.target.value)}
              placeholder="Agenda, location, meeting link…" className="mt-1.5" data-testid="event-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="event-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.title.trim() || !form.date} data-testid="event-save-btn">
            {saving ? 'Saving…' : 'Save Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
